/**
 * The branch the active repository is on, always in sight next to it — and, on the desktop, a click
 * away from switching, as in VS Code: pick a local or remote branch, fetch, or create a new one.
 *
 * In the browser it shows the branch the desktop last reported (the repository snapshot), and says
 * that switching happens in the desktop app. Either way it warns when teammates' agents work in the
 * same repository on another branch.
 *
 * When the repository is linked but not on this machine — the app looked for it by its git remote
 * and did not find it (docs/refactor/53) — the desktop asks where it is: point at its folder, or
 * download it with the person's own git credentials. Switching project at the top opens that
 * question by itself. A folder of another repository is refused; a refused clone means they have no
 * access yet — the same e-mail has to be given access on GitHub. Either way the project then opens
 * in Trabalho.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Download, FolderOpen, GitBranch, Loader2, Plus, RefreshCw } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { isDesktop, projectAdd, repoBranches, repoCheckout, repoClone, repoForget, repoPick } from '../lib/desktop'
import { allFolders, findFoldersHere, folderFor, folderMatches, FOLDERS_CHANGED, rememberFolder } from '../lib/repoFolders'
import { ASK_FOLDER_EVENT, showOpenProject } from '../lib/followProject'
import { useRepoFiles } from '../lib/repoSource'
import { othersOnOtherBranches } from '../lib/branches'
import { createPortal } from 'react-dom'
import { Modal } from './Modal'
import type { RepositoryLink } from '../lib/types'

export function BranchPicker({ repository }: { repository: RepositoryLink }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const desktop = isDesktop()
  const [open, setOpen] = useState(false)
  const [asked, setAsked] = useState(false)
  const [filter, setFilter] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [folder, setFolder] = useState<string | null>(() => (desktop ? folderFor(repository.name) : null))
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!desktop) return
    const read = () => setFolder(folderFor(repository.name))
    read()
    // Found by the search when the app opened, or picked in Trabalho: shown at once.
    window.addEventListener(FOLDERS_CHANGED, read)
    return () => window.removeEventListener(FOLDERS_CHANGED, read)
  }, [repository.name, desktop])

  // Switching project at the top, to one not found here: ask where it is (lib/followProject.ts).
  useEffect(() => {
    const onAsk = (event: Event) => {
      if ((event as CustomEvent<string>).detail === repository.id) setAsked(true)
    }
    window.addEventListener(ASK_FOLDER_EVENT, onAsk)
    return () => window.removeEventListener(ASK_FOLDER_EVENT, onAsk)
  }, [repository.id])

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: view } = useRepoFiles(repository)
  const { data: branches, refetch, isFetching } = useQuery({
    queryKey: ['repo-branches', folder],
    enabled: Boolean(desktop && folder),
    queryFn: () => repoBranches(folder!),
    staleTime: 15_000,
  })
  const { data: live } = useQuery({ queryKey: ['live'], queryFn: () => api.dashboard.live(), staleTime: 15_000, refetchInterval: 30_000 })

  const current = branches?.current ?? view?.branch ?? null
  const others = useMemo(() => othersOnOtherBranches(live ?? [], repository.name, current, me?.developerId), [live, repository.name, current, me])

  // Closes on a click outside or Escape, as a menu does.
  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => !box.current?.contains(event.target as Node) && setOpen(false)
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['repo-branches'] })
    void queryClient.invalidateQueries({ queryKey: ['repo-files'] })
  }

  const checkout = useMutation({
    mutationFn: ({ name, create }: { name: string; create: boolean }) => repoCheckout(folder!, name, create),
    onSuccess: () => {
      setOpen(false)
      setFilter('')
      setProblem(null)
      reload()
    },
    onError: (error) => setProblem(String(error) === 'invalid-branch' ? t('branch.invalid') : String(error)),
  })

  const bring = useMutation({
    mutationFn: async (how: 'clone' | 'pick' | 'search') => {
      if (how === 'search') {
        await findFoldersHere([repository], true)
        const found = folderFor(repository.name)
        if (!found) throw new Error('not-found')
        return { path: found }
      }
      const picked = how === 'clone' ? await repoClone(repository.remoteUrl!) : await repoPick()
      if (picked && !folderMatches(repository, picked.remote_url)) {
        // Not left allowed for nothing — unless it is another project's folder already.
        if (!allFolders().includes(picked.path)) await repoForget(picked.path).catch(() => {})
        throw new Error('other-repository')
      }
      if (picked) rememberFolder(repository.name, picked.path)
      return picked
    },
    onSuccess: (picked) => {
      if (!picked) return
      setProblem(null)
      setFolder(picked.path)
      setOpen(false)
      setAsked(false)
      // The project opens in Trabalho, with its files, as if opened there.
      void projectAdd(picked.path).then((project) => project && showOpenProject(queryClient, project)).catch(() => {})
      reload()
    },
    onError: (error) => {
      const code = error instanceof Error ? error.message : String(error)
      setProblem(
        code === 'no-access' ? t('branch.noAccess')
          : code === 'exists' ? t('branch.exists')
          : code === 'not-a-repository' ? t('branch.notRepo')
          : code === 'other-repository' ? t('branch.otherRepo', { name: repository.name })
          : code === 'not-found' ? t('branch.bringBody', { name: repository.name })
          : code,
      )
    },
  })

  // ---- desktop, repository not on this machine: bring it here
  // ---- desktop, repository not on this machine: where is it?
  const whereIsIt = (
    <>
      <p className="tiny muted" style={{ margin: 0 }}>
        {t('branch.bringBody', { name: repository.name })}
      </p>
      {repository.remoteUrl && <code className="tiny mono branch-menu__url">{repository.remoteUrl}</code>}
      {problem && <p className="tiny branch-menu__problem" role="alert">{problem}</p>}
      {bring.isPending && bring.variables === 'search' && (
        <p className="tiny muted" style={{ margin: 0 }}><Loader2 size={12} className="spin" /> {t('branch.searching')}</p>
      )}
      <div className="row row--tight row--wrap">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => bring.mutate('pick')} disabled={bring.isPending}>
          <FolderOpen size={13} /> {t('branch.pickFolder')}
        </button>
        {repository.remoteUrl && (
          <button type="button" className="btn btn--sm" onClick={() => bring.mutate('clone')} disabled={bring.isPending}>
            {bring.isPending && bring.variables === 'clone' ? <Loader2 size={13} className="spin" /> : <Download size={13} />} {t('branch.clone')}
          </button>
        )}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => bring.mutate('search')} disabled={bring.isPending}>
          <RefreshCw size={13} /> {t('branch.searchAgain')}
        </button>
      </div>
      {repository.remoteUrl && <p className="tiny muted branch-menu__access">{t('branch.accessNote')}</p>}
    </>
  )

  if (desktop && !folder) {
    return (
      <div className="branch-picker" ref={box}>
        <button type="button" className="btn btn--sm branch-picker__missing" onClick={() => setOpen(!open)} aria-expanded={open}>
          <AlertTriangle size={13} /> {t('branch.notHere')}
        </button>
        {open && (
          <div className="branch-menu panel" role="dialog" aria-label={t('branch.bringTitle', { name: repository.name })}>
            <strong className="small">{t('branch.bringTitle', { name: repository.name })}</strong>
            {whereIsIt}
          </div>
        )}
        {/* Asked by switching project at the top: a dialog, seen at any width (the header hides this picker when narrow). */}
        {asked && createPortal(
          <Modal title={t('branch.bringTitle', { name: repository.name })} onClose={() => setAsked(false)}>
            <div className="stack stack--tight">{whereIsIt}</div>
          </Modal>,
          document.body,
        )}
      </div>
    )
  }

  const warning = others.length > 0 && (
    <span className="branch-picker__warn" title={others.map((o) => `${o.developerName}: ${o.branch}`).join('\n')}>
      <AlertTriangle size={12} /> {t('branch.othersOn', { count: others.length })}
    </span>
  )

  // ---- in the browser: what the desktop last reported, read-only
  if (!desktop) {
    return (
      <div className="branch-picker">
        <span className="branch-picker__current" title={t('branch.webHint')}>
          <GitBranch size={13} /> <span className="mono">{current ?? t('branch.unknown')}</span>
        </span>
        {warning}
      </div>
    )
  }

  const needle = filter.trim().toLowerCase()
  const match = (name: string) => !needle || name.toLowerCase().includes(needle)
  const exact = [...(branches?.local ?? []), ...(branches?.remote ?? [])].some((b) => b.toLowerCase() === needle || b.toLowerCase().endsWith(`/${needle}`))

  return (
    <div className="branch-picker" ref={box}>
      <button type="button" className="btn btn--ghost btn--sm branch-picker__current" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="listbox" title={t('branch.switchHint')}>
        <GitBranch size={13} /> <span className="mono">{current ?? '…'}</span>
      </button>
      {warning}
      {open && (
        <div className="branch-menu panel">
          <div className="row row--tight">
            <input
              className="input input--sm"
              autoFocus
              value={filter}
              placeholder={t('branch.filter')}
              aria-label={t('branch.filter')}
              onChange={(event) => setFilter(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && needle && !exact) checkout.mutate({ name: filter.trim(), create: true })
              }}
            />
            <button type="button" className="btn btn--ghost btn--icon btn--sm" title={t('branch.fetch')} aria-label={t('branch.fetch')} onClick={() => void repoBranches(folder!, true).then(() => refetch())}>
              <RefreshCw size={13} className={isFetching ? 'spin' : undefined} />
            </button>
          </div>
          {problem && <p className="tiny branch-menu__problem" role="alert">{problem}</p>}
          <ul className="branch-menu__list" role="listbox" aria-label={t('branch.title')}>
            {needle && !exact && (
              <li>
                <button type="button" className="branch-menu__item" onClick={() => checkout.mutate({ name: filter.trim(), create: true })}>
                  <Plus size={12} /> {t('branch.create', { name: filter.trim() })}
                </button>
              </li>
            )}
            {(branches?.local ?? []).filter(match).map((name) => (
              <li key={name}>
                <button type="button" role="option" aria-selected={name === current} className={`branch-menu__item${name === current ? ' is-current' : ''}`} onClick={() => name !== current && checkout.mutate({ name, create: false })}>
                  <GitBranch size={12} /> <span className="mono">{name}</span>
                  {others.filter((o) => o.branch === name).map((o) => <span key={o.developerId} className="tiny muted">· {o.developerName}</span>)}
                </button>
              </li>
            ))}
            {(branches?.remote ?? []).filter(match).length > 0 && <li className="branch-menu__group tiny muted">{t('branch.remote')}</li>}
            {(branches?.remote ?? []).filter(match).map((name) => (
              <li key={name}>
                <button type="button" role="option" aria-selected={false} className="branch-menu__item" onClick={() => checkout.mutate({ name, create: false })}>
                  <GitBranch size={12} /> <span className="mono">{name}</span>
                </button>
              </li>
            ))}
          </ul>
          {checkout.isPending && <p className="tiny muted" style={{ margin: 0 }}><Loader2 size={12} className="spin" /> {t('branch.switching')}</p>}
        </div>
      )}
    </div>
  )
}
